/**
 * Projects Section - Featured Work
 * =================================
 * 
 * OPTIMIZED VERSION:
 * - No scroll-triggered loading - loads immediately
 * - Uses images (P1.png - P9.png) instead of iframes for preview
 */

import React, { useState, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { projects } from '../../data/projects';
import { config } from '../../data/config';
import './Projects.css';

// ============================================
// PROJECT CARD COMPONENT - LOADS IMMEDIATELY
// ============================================
const ProjectCard = memo(({ project, index, onClick }) => {
  const cardRef = useRef(null);

  return (
    <div
      ref={cardRef}
      className={`project-card project-${project.color} in-view`}
      onClick={() => onClick(project)}
    >
      {/* Preview Section - Using Image */}
      <div className="project-visual">
        <div className="project-preview">
          <img
            src={`/assets/projects/P${index + 1}.png`}
            alt={`${project.title} preview`}
            loading="lazy"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="preview-fallback">
            <i className={`fas ${project.icon}`} />
          </div>
          <div className="preview-overlay">
            <div className="preview-icon">
              <i className="fas fa-expand" />
            </div>
          </div>
        </div>
        <div className="project-category">{project.category}</div>
      </div>
      
      <div className="project-info">
        <h3>{project.title}</h3>
        <p className="project-tagline">{project.tagline}</p>
        <p className="project-desc">{project.description}</p>
        
        <div className="project-stats">
          {Object.entries(project.stats).slice(0, 3).map(([key, val]) => (
            <div key={key} className="p-stat">
              <span className="p-val">{val}</span>
              <span className="p-key">{key}</span>
            </div>
          ))}
        </div>
        
        <div className="project-footer">
          <span>View Project</span>
          <i className="fas fa-arrow-right" />
        </div>
      </div>
      
      <div className="project-glow" />
    </div>
  );
});

ProjectCard.displayName = 'ProjectCard';

// ============================================
// PROJECT MODAL COMPONENT
// ============================================
const ProjectModal = ({ project, onClose }) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const modalRef = useRef(null);

  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <motion.div
      className="project-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <motion.div
        ref={modalRef}
        className={`project-modal modal-${project.color}`}
        initial={{ opacity: 0, y: 50, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 30, scale: 0.95 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button className="modal-close" onClick={onClose} aria-label="Close">
          <i className="fas fa-times" />
        </button>

        {/* Modal Header - Project Details */}
        <div className="modal-header">
          <div className="modal-header-top">
            <div className="modal-icon">
              <i className={`fas ${project.icon}`} />
            </div>
            <div className="modal-title-area">
              <span className="modal-category">{project.category}</span>
              <h2>{project.title}</h2>
              <p className="modal-tagline">{project.tagline}</p>
            </div>
          </div>
          
          <p className="modal-description">{project.heroDescription}</p>

          {/* Stats Row */}
          <div className="modal-stats">
            {Object.entries(project.stats).map(([key, val]) => (
              <div key={key} className="modal-stat">
                <span className="stat-value">{val}</span>
                <span className="stat-label">{key}</span>
              </div>
            ))}
          </div>

          {/* Tech Stack */}
          <div className="modal-tech">
            {project.tech.map((t, i) => (
              <span key={i} className="tech-tag">{t}</span>
            ))}
          </div>
        </div>

        {/* Modal Body - iFrame */}
        <div className="modal-iframe-container">
          <div className="iframe-header">
            <span className="iframe-label">
              <i className="fas fa-desktop" />
              Live Preview
            </span>
            <a 
              href={project.htmlFile} 
              target="_blank" 
              rel="noopener noreferrer"
              className="iframe-external"
            >
              <i className="fas fa-external-link-alt" />
              Open Full
            </a>
          </div>
          
          {!iframeLoaded && (
            <div className="iframe-loader">
              <div className="loader-spinner"></div>
              <span>Loading project...</span>
            </div>
          )}
          
          <iframe
            src={project.htmlFile}
            title={project.title}
            className={`project-iframe ${iframeLoaded ? 'loaded' : ''}`}
            onLoad={() => setIframeLoaded(true)}
          />
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <a
            href={config.social.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-cta"
          >
            <i className="fab fa-whatsapp" />
            <span>Build Something Similar</span>
          </a>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============================================
// MAIN PROJECTS COMPONENT - NO SCROLL TRIGGERS
// ============================================
const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);

  return (
    <section id="projects" className="projects-section">
      {/* Background with particles */}
      <div className="projects-bg">
        {/* Subtle glows */}
        <div className="projects-glow projects-glow-1" />
        <div className="projects-glow projects-glow-2" />
        
        {/* CSS particles - 20 particles */}
        <div className="projects-particles">
          {[...Array(20)].map((_, i) => (
            <span 
              key={i}
              className="particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                '--size': `${4 + Math.random() * 6}px`,
                '--duration': `${12 + Math.random() * 15}s`,
                '--delay': `${Math.random() * 8}s`
              }}
            />
          ))}
        </div>
        <div className="projects-grid-bg" />
        <div className="projects-vignette" />
      </div>

      <div className="projects-container">
        {/* Section Header - Always visible */}
        <div className="projects-header in-view">
          <div className="section-badge">
            <span className="badge-dot" />
            <span>Our Work</span>
          </div>
          <h2 className="section-title">
            Featured <span className="text-highlight">Projects</span>
          </h2>
          <p className="section-subtitle">
            Explore our portfolio of successful blockchain & software projects
          </p>
        </div>

        {/* Projects Grid - All cards visible immediately */}
        <div className="projects-grid">
          {projects.map((project, i) => (
            <ProjectCard
              key={project.id}
              project={project}
              index={i}
              onClick={setSelectedProject}
            />
          ))}
        </div>

        {/* CTA Section */}
        <div className="projects-cta in-view">
          <p>Ready to start your project?</p>
          <a
            href={config.social.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
          >
            <i className="fab fa-whatsapp" />
            Let's Build Together
          </a>
        </div>
      </div>

      {/* Project Modal */}
      <AnimatePresence>
        {selectedProject && (
          <ProjectModal
            key="project-modal"
            project={selectedProject}
            onClose={() => setSelectedProject(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default Projects;
